import type { ComboboxItem } from "@mantine/core";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useGetApi, usePostApi } from "src/hooks";
import { getYachtId } from "src/utils";

import { mapCheckListLogbooks } from "./CheckList.utils";
import type { ICheckListItem } from "./CheckLists.types";

interface ICheckListsContext {
  logbooks: ComboboxItem[];
  tileLogbooks: ComboboxItem[];
  defaultTileLogbooks: ComboboxItem[];
  setTileLogbooks: (logbooks: ComboboxItem[]) => void;
  checklists: ICheckListItem[];
  defaultChecklists: ICheckListItem[];
  setChecklists: (checklists: ICheckListItem[]) => void;
  fetchChecklists: (body?: Record<string, any>, reset?: boolean) => void;
  removeChecklist: (cid: string) => void;
  resetSearch?: number;
  setResetSearch?: (resetSearch: number) => void;
  resetTileSearch?: number;
  setResetTileSearch?: (resetTileSearch: number) => void;
}

const CheckListsContext = createContext<ICheckListsContext | null>(null);

export const CheckListsProvider = ({ children }: { children: ReactNode }) => {
  const [checklists, setChecklists] = useState<any[]>([]);
  const [defaultChecklists, setDefaultChecklists] = useState<any[]>([]);
  const [resetSearch, setResetSearch] = useState(0);
  const [tileLogbooks, setTileLogbooks] = useState<ComboboxItem[]>([]);
  const [defaultTileLogbooks, setDefaultTileLogbooks] = useState<
    ComboboxItem[]
  >([]);
  const [resetTileSearch, setResetTileSearch] = useState(0);
  const { lbid } = useParams();
  const isSearchingRef = useRef(false);
  const isTileSearchingRef = useRef(false);

  const { data: logbooks, refetch: fetchLogbooks } = useGetApi({
    request: "get-checklists-logbooks",
    body: { params: { yachtid: getYachtId(), ...(lbid && { lbid }) } },
    mapper: mapCheckListLogbooks,
    fetchOnInit: false,
  });

  const mapCheckList = useCallback((data: any) => {
    return data?.list ?? [];
  }, []);

  const { refetch: fetchChecklists } = useGetApi({
    request: "get-checklists",
    body: { params: { yachtid: getYachtId() } },
    fetchOnInit: false,
    mapper: mapCheckList,
  });

  const { post } = usePostApi();

  const handleResetSearch = useCallback(() => {
    setResetSearch(Math.random());
    setTimeout(() => {
      isSearchingRef.current = false;
      setResetSearch(0);
    }, 0);
  }, []);

  const removeChecklist = useCallback(
    async (cid: string) => {
      await post({
        request: "archive-checklists",
        body: {
          params: {
            yachtid: getYachtId(),
            list: [cid],
          },
        },
        onSuccess: () => {
          handleResetSearch();
          setChecklists(prev => prev?.filter(i => i.cid !== cid));
          // Also update defaultChecklists to keep them in sync
          setDefaultChecklists(prev => prev?.filter(i => i.cid !== cid));
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
    [post],
  );

  useEffect(() => {
    void fetchLogbooks();
  }, [fetchLogbooks]);

  useEffect(() => {
    const tileSource = logbooks ?? [];
    setDefaultTileLogbooks(tileSource);
    if (!isTileSearchingRef.current) {
      setTileLogbooks(tileSource);
    }
  }, [logbooks]);

  const handleRefetchChecklists = useCallback(
    async (params: any, reset?: boolean) => {
      const data = await fetchChecklists({ params });
      setDefaultChecklists(data);

      // Only update the displayed checklist if we're not currently searching
      if (!isSearchingRef.current) {
        setChecklists(data);
      }

      if (reset) {
        handleResetSearch();
      }

      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
    [fetchChecklists],
  );

  const value = useMemo(
    () => ({
      logbooks: logbooks ?? [],
      tileLogbooks,
      defaultTileLogbooks,
      setTileLogbooks: (newTileLogbooks: ComboboxItem[]) => {
        isTileSearchingRef.current =
          newTileLogbooks.length !== defaultTileLogbooks.length &&
          defaultTileLogbooks.length > 0;
        setTileLogbooks(newTileLogbooks);
      },
      checklists: checklists ?? [],
      defaultChecklists: defaultChecklists ?? [],
      setChecklists: (newChecklists: ICheckListItem[]) => {
        isSearchingRef.current =
          newChecklists.length !== defaultChecklists.length &&
          defaultChecklists.length > 0;
        setChecklists(newChecklists);
      },
      fetchChecklists: handleRefetchChecklists,
      removeChecklist,
      resetSearch,
      setResetSearch,
      resetTileSearch,
      setResetTileSearch,
    }),
    [
      checklists,
      defaultChecklists,
      defaultTileLogbooks,
      handleRefetchChecklists,
      logbooks,
      removeChecklist,
      resetSearch,
      resetTileSearch,
      tileLogbooks,
    ],
  );

  return (
    <CheckListsContext.Provider value={value}>
      {children}
    </CheckListsContext.Provider>
  );
};

export const useChecklistsContext = () => {
  const context = useContext(CheckListsContext);
  if (!context) {
    throw new Error(
      "useChecklistsContext must be used within a CheckListsProvider",
    );
  }
  return context;
};
